import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { motion } from "framer-motion";
import { FaRegCalendarAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { TextGenerateEffect } from "./ui/Text-generate-effect";
import ScheduledSubjectsModal from "./modals/ScheduledSubjectsModal";
import useAuth from "../hooks/useAuth";
// Container animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 2 } },
};

// Dynamic variants based on language direction (LTR/RTL)
const directionVariants = (isRTL, side) => ({
  hidden: {
    x:
      side === "left"
        ? isRTL
          ? "100vw"
          : "-100vw"
        : isRTL
          ? "-100vw"
          : "100vw",
    opacity: 0,
  },
  visible: { x: 0, opacity: 1, transition: { duration: 2 } },
});

const Showcase = () => {
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [openScheduledModal, setOpenScheduledModal] = useState(false);

  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setCurrentLanguage(lng);
    };

    i18n.on("languageChanged", handleLanguageChanged);

    // Cleanup listener on unmount
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [i18n]);

  // Check if language is RTL (Pashto or Persian)
  const isRTL = ["ps", "pr"].includes(i18n.language);

  return (
    <>
      <section className="relative -top-[4.8rem] flex items-center justify-center overflow-hidden sm:h-screen h-[calc(100vh-3rem)] isolate bg-showcases bg-cover bg-center bg-no-repeat">
        <motion.div
          className="grid max-w-lg grid-cols-1 lg:grid-cols-2 px-4 mx-auto sm:max-w-5xl sm:px-6 lg:max-w-7xl lg:px-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Left content */}
          <motion.div
            className="max-w-2xl py-4"
            variants={directionVariants(isRTL, "left")}
          >
            <div className="my-5">
              <TextGenerateEffect
                key={currentLanguage} // Key based on language
                className="sm:text-6xl lg:text-4xl text-2xl font-bold lg:mt-3 tracking-widest"
                words={t("hero-slogan")}
              />
            </div>

            {/* <p className="my-10">
                        {t("hero-description")}
                    </p> */}

            {/* <Link to="/courses" className="rounded-md py-2 px-4 border border-teal-500 hover:bg-teal-500 hover:text-white transition duration-200 ease-in-out">
                        <span className="px-1">{t("popular-courses")}</span>
                        <FaArrowRight size={12} className="inline-flex rtl:rotate-180" />
                    </Link> */}

            {/* Here I want to create a button for Modal  */}
            <button
              type="button"
              onClick={() => setOpenScheduledModal(true)}
              className="group mt-3 inline-flex w-auto items-center justify-between gap-3 rounded-2xl border border-indigo-400/70 bg-white/95 px-4 py-3 text-sm font-semibold text-indigo-900 shadow-lg shadow-indigo-200/60 backdrop-blur-sm transition duration-200 ease-in-out hover:-translate-y-0.5 hover:border-indigo-500 hover:bg-indigo-500 hover:text-white hover:shadow-indigo-400/40"
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <span className="relative flex h-3 w-3">
                  <motion.span
                    className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"
                    animate={{ scale: [1, 1.8, 1], opacity: [0.8, 0, 0.8] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-600 ring-4 ring-rose-200/70 transition duration-200 group-hover:bg-white group-hover:ring-white/30" />
                </span>
                <span>{t("nav.reserved-subjects")}</span>
              </span>
              <FaRegCalendarAlt
                size={16}
                className="transition duration-200 group-hover:rotate-12"
              />
            </button>

            {/* <div className="flex gap-4">
                        <a
                            href="/standards/video-standards-1.pdf"
                            download
                            className="text-sm flex items-center gap-2 rounded-md py-2 px-3 border border-indigo-500 hover:bg-indigo-500 hover:text-white transition duration-200 ease-in-out"
                        >
                            <span>{t("video-standards")} - 1</span>
                            <FaDownload size={14} />
                        </a>

                        <a
                            href="/standards/video-standards-2.pdf"
                            download
                            className="text-sm flex items-center gap-2 rounded-md py-2 px-3 border border-indigo-500 hover:bg-indigo-500 hover:text-white transition duration-200 ease-in-out"
                        >
                            <span>{t("video-standards")} - 2</span>
                            <FaDownload size={14} />

                        </a>
                    </div> */}
          </motion.div>

          {/* Right content */}
          <motion.div
            className="my-auto sm:ml-auto rtl:sm:mr-auto rtl:sm:ml-0 sm:w-full lg:blocks hidden lg:block"
            variants={directionVariants(isRTL, "right")}
          >
            <div className="px-6">
              <img src="/hero-img.webp" alt="Hero" loading="lazy" />
            </div>
          </motion.div>
        </motion.div>

        {/* SVG Background */}
        <svg
          className="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)] -z-10"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="e813992c-7d03-4cc4-a2bd-151760b470a0"
              width="200"
              height="200"
              x="50%"
              y="-1"
              patternUnits="userSpaceOnUse"
            >
              <path d="M100 200V.5M.5 .5H200" fill="none"></path>
            </pattern>
          </defs>
          <svg x="50%" y="-1" className="overflow-visible fill-gray-50">
            <path
              d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
              strokeWidth="0"
            ></path>
          </svg>
          <rect
            width="100%"
            height="100%"
            strokeWidth="0"
            fill="url(#e813992c-7d03-4cc4-a2bd-151760b470a0)"
          ></rect>
        </svg>
      </section>
      <ScheduledSubjectsModal
        open={openScheduledModal}
        handleOpen={() => setOpenScheduledModal((prev) => !prev)}
        accessToken={accessToken}
      />
    </>
  );
};

export default Showcase;
